import { useState, useEffect } from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  Linkedin,
  CheckCircle,
  Shield,
  ExternalLink,
  ArrowRight,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp, ZoomIn } from 'react-native-reanimated';
import { reportError } from '@/lib/errors';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { useProfileStore } from '@/lib/state/profile-store';
import { OnboardingLayout, INVESTOR_ONBOARDING_STEPS } from '@/components/OnboardingLayout';
import Badge from '@/components/Badge';
import { useAuth } from '@/lib/supabase/auth';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { mode } from '@/lib/config/app-mode';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAlert } from '@/components/AlertDialog';

type VerificationState = 'idle' | 'connecting' | 'success' | 'error';

export default function LinkedInVerifyScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { isDesktop } = useResponsive();
  const { show } = useAlert();

  const user = useProfileStore((s) => s.user);
  const investorProfile = useProfileStore((s) => s.investorProfile);
  const setInvestorProfile = useProfileStore((s) => s.setInvestorProfile);

  // Get real auth context
  const { signInWithLinkedIn, getLinkedInData, profile, refreshProfile, user: authUser } = useAuth();
  const linkedInData = getLinkedInData();

  const [state, setState] = useState<VerificationState>('idle');
  const [persisting, setPersisting] = useState(false);

  // A REAL LinkedIn OAuth identity is present (not fabricated / not mock).
  const hasLinkedInOAuth = linkedInData.isLinkedInAuth && !!linkedInData.linkedInUrl;
  // The verified badge has EXACTLY ONE source of truth: the DB flag
  // `profiles.linkedin_verified`. Never derive "Verified" from Zustand or from
  // a mock/simulated success — an unverified user must see no badge.
  const isVerified = profile?.linkedin_verified === true;

  // When a real OAuth identity exists but the DB flag isn't set yet, persist it
  // (AWAITED) then refresh the profile so the badge reflects real, durable state.
  useEffect(() => {
    if (!hasLinkedInOAuth || isVerified || persisting) return;
    if (!isSupabaseConfigured() || !authUser?.id) return;
    let cancelled = false;
    setPersisting(true);
    (async () => {
      try {
        const { error } = await (supabase.from('profiles') as any)
          .update({
            linkedin_verified: true,
            linkedin_verified_at: new Date().toISOString(),
            linkedin_url: linkedInData.linkedInUrl,
          })
          .eq('id', authUser.id);
        if (error) throw error;
        await refreshProfile();
        // Mirror into the investor store for non-badge UI; the badge itself
        // always reads the DB flag, never this mirror.
        if (investorProfile && !investorProfile.linkedInVerified) {
          setInvestorProfile({
            ...investorProfile,
            linkedInVerified: true,
            linkedInVerifiedAt: new Date().toISOString(),
            linkedInUrl: linkedInData.linkedInUrl || undefined,
          });
        }
      } catch (err) {
        reportError(err, { context: 'LinkedInVerify.persistVerified', level: 'warning' });
      } finally {
        if (!cancelled) setPersisting(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLinkedInOAuth, isVerified, authUser?.id]);

  const handleVerify = async () => {
    // Demo mode cannot perform a real LinkedIn check — never fabricate success.
    if (mode.isMock) {
      show({
        title: 'LinkedIn Verification',
        message: 'Verifying connects your real LinkedIn account and isn\'t available in demo mode.',
        buttons: [{ text: 'OK', onPress: () => setState('idle') }],
      });
      return;
    }

    setState('connecting');

    // Use real LinkedIn OAuth via Supabase. Success is handled by the auth
    // state-change listener + the persist effect above (which writes the DB flag).
    const { error } = await signInWithLinkedIn();

    if (error) {
      reportError(error, { context: 'LinkedInVerify.handleVerify' });
      setState('error');
      setTimeout(() => setState('idle'), 2000);
    }
  };

  // LinkedIn is an OPTIONAL sub-step launched from the identity screen — return
  // there whether the user verified or skipped (never route into portfolio,
  // which is now post-onboarding only).
  const handleContinue = () => {
    router.back();
  };

  const handleSkip = () => {
    router.back();
  };

  // Content for the verification UI
  const verificationContent = (
    <View className="items-center">
      {isVerified ? (
        // Success State
        <Animated.View
          entering={ZoomIn.delay(200).duration(600).springify()}
          className="items-center w-full"
        >
          <View
            className="w-24 h-24 rounded-full items-center justify-center mb-6"
            style={{ backgroundColor: colors.traffic.greenLight }}
          >
            <CheckCircle size={48} color={colors.traffic.green} />
          </View>

          <Text
            style={{ color: colors.text.primary }}
            className="text-2xl font-bold text-center mb-2"
          >
            Verified!
          </Text>

          {/* Show LinkedIn profile info if available */}
          {linkedInData.isLinkedInAuth && linkedInData.name && (
            <View className="items-center mb-4">
              <Text
                style={{ color: colors.text.secondary }}
                className="text-base text-center"
              >
                Signed in as
              </Text>
              <Text
                style={{ color: colors.text.primary }}
                className="text-lg font-semibold text-center"
              >
                {linkedInData.name}
              </Text>
              {linkedInData.email && (
                <Text
                  style={{ color: colors.text.tertiary }}
                  className="text-sm text-center"
                >
                  {linkedInData.email}
                </Text>
              )}
            </View>
          )}

          <Text
            style={{ color: colors.text.secondary }}
            className="text-base text-center mb-6"
          >
            Your LinkedIn identity has been confirmed
          </Text>

          <Badge type="linkedin-verified" size="lg" />

          <View
            className="mt-8 p-4 rounded-xl w-full"
            style={{ backgroundColor: colors.surface.primary }}
          >
            <View className="flex-row items-center gap-3">
              <Shield size={20} color={colors.traffic.green} />
              <Text style={{ color: colors.text.secondary }} className="text-sm flex-1">
                Your badge will be visible to startups, building trust in your profile
              </Text>
            </View>
          </View>

          {/* Continue Button */}
          <View className="w-full mt-8">
            <AnimatedPressable
              onPress={handleContinue}
              className="rounded-xl py-4 flex-row items-center justify-center gap-2"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                Complete Setup
              </Text>
              <ArrowRight size={20} color={colors.text.inverse} />
            </AnimatedPressable>
          </View>
        </Animated.View>
      ) : (
        // Idle/Connecting/Error State
        <Animated.View
          entering={FadeInUp.delay(200).duration(600)}
          className="items-center w-full"
        >
          <View
            className="w-24 h-24 rounded-full items-center justify-center mb-6"
            style={{
              backgroundColor: state === 'error' ? colors.traffic.redLight : '#E7F3FF',
            }}
          >
            {state === 'connecting' ? (
              <ActivityIndicator size="large" color="#0A66C2" />
            ) : (
              <Linkedin size={48} color={state === 'error' ? colors.traffic.red : '#0A66C2'} />
            )}
          </View>

          <Text
            style={{ color: colors.text.primary }}
            className="text-2xl font-bold text-center mb-2"
          >
            {state === 'connecting'
              ? 'Connecting...'
              : state === 'error'
              ? 'Connection Failed'
              : 'Verify with LinkedIn'}
          </Text>
          <Text
            style={{ color: colors.text.secondary }}
            className="text-base text-center leading-relaxed px-4"
          >
            {state === 'connecting'
              ? 'Please wait while we verify your account'
              : state === 'error'
              ? 'Something went wrong. Please try again.'
              : 'Connect your LinkedIn account to verify your identity and build trust with founders'}
          </Text>

          {(state === 'idle' || state === 'error') && (
            <View className="mt-8 gap-4 w-full">
              <AnimatedPressable
                onPress={handleVerify}
                className="flex-row items-center justify-center gap-3 py-4 rounded-xl"
                style={{ backgroundColor: '#0A66C2' }}
              >
                <Linkedin size={22} color="white" />
                <Text className="text-white font-semibold text-base">
                  {state === 'error' ? 'Try Again' : 'Connect LinkedIn'}
                </Text>
                <ExternalLink size={16} color="white" />
              </AnimatedPressable>

              <View
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <Text style={{ color: colors.text.tertiary }} className="text-xs text-center">
                  We only access your public profile information.{'\n'}
                  We never post on your behalf.
                </Text>
              </View>

              {/* Skip Option */}
              <Pressable
                onPress={handleSkip}
                className="py-3 items-center"
              >
                <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
                  Skip for now
                </Text>
              </Pressable>
            </View>
          )}
        </Animated.View>
      )}
    </View>
  );

  // Desktop layout with OnboardingLayout
  if (isWeb && isDesktop) {
    return (
      <OnboardingLayout
        currentStep="linkedin"
        steps={INVESTOR_ONBOARDING_STEPS}
        title="LinkedIn Verification"
        subtitle="Optional: Verify your identity to build trust"
        userType="investor"
        entityName={user?.name || undefined}
      >
        {verificationContent}
      </OnboardingLayout>
    );
  }

  // Mobile layout
  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        {/* Header */}
        <Animated.View
          entering={FadeInDown.delay(100).duration(500)}
          className="px-5 pt-4 pb-4"
        >
          <View className="flex-row items-center justify-between">
            <Pressable
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <ChevronLeft size={24} color={colors.text.primary} />
            </Pressable>
            <View className="flex-row gap-2">
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.primary[500] }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.primary[500] }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{
                  backgroundColor: isVerified
                    ? colors.primary[500]
                    : colors.border.light,
                }}
              />
            </View>
            <View className="w-10" />
          </View>
        </Animated.View>

        {/* Content */}
        <View className="flex-1 px-6 justify-center">
          {verificationContent}
        </View>
      </SafeAreaView>
    </View>
  );
}
